import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SceneBack } from '@/shared/components/SceneBack';
import { CHARACTERS, portraitFor } from '../data/characters';
import {
  listWorldviews,
  getCurrentContent,
  type SavedWorldview,
} from '../../worldview/lib/storage';

/**
 * Watch flow — pick two debaters, then step into the arena.
 *
 * Debaters can be either historical figures or user-authored worldviews.
 * As soon as both slots are filled, we auto-advance to /arena/watch/debate.
 *
 * Custom worldviews prompt for a stage name on selection so the debater
 * shown to viewers isn't the worldview's raw saved name (which often reads
 * like "Arthur Garzon's Brain"). The override is per-session only.
 */

function worldviewDisplayName(wv: SavedWorldview): string {
  if (wv.name) return wv.name;
  const content = getCurrentContent(wv);
  const match = content.match(/^#\s+(.+)$/m);
  return match?.[1]?.trim() ?? 'My Worldview';
}

export function Watch() {
  const navigate = useNavigate();
  const [leftId, setLeftId] = useState<string | null>(null);
  const [rightId, setRightId] = useState<string | null>(null);
  const advanceTimerRef = useRef<number | null>(null);

  const worldviews = useMemo(() => listWorldviews(), []);

  // Per-session stage names for custom worldviews, keyed by worldview id.
  // Sticky across deselect/re-select so the user doesn't lose their override.
  const [stageNames, setStageNames] = useState<Record<string, string>>({});
  const [namingId, setNamingId] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = 'The Stands — The Arena';
  }, []);

  const selectedIds = useMemo(() => {
    const s = new Set<string>();
    if (leftId) s.add(leftId);
    if (rightId) s.add(rightId);
    return s;
  }, [leftId, rightId]);

  const hasBothSelected = leftId !== null && rightId !== null;

  const displayNameFor = (wv: SavedWorldview): string =>
    stageNames[wv.id] ?? worldviewDisplayName(wv);

  const getDebaterInfo = (id: string | null) => {
    if (!id) return null;
    const char = CHARACTERS.find((c) => c.id === id);
    if (char) return { name: char.name, era: char.era, worldview: undefined };
    const wv = worldviews.find((w) => w.id === id);
    if (wv) return { name: displayNameFor(wv), era: 'Your worldview', worldview: getCurrentContent(wv) };
    return null;
  };

  const left = getDebaterInfo(leftId);
  const right = getDebaterInfo(rightId);

  // Auto-advance when both are picked.
  useEffect(() => {
    if (!hasBothSelected || !leftId || !rightId) return;
    advanceTimerRef.current = window.setTimeout(() => {
      const leftWv = worldviews.find((w) => w.id === leftId);
      const rightWv = worldviews.find((w) => w.id === rightId);
      navigate('/arena/watch/debate', {
        state: {
          leftId,
          rightId,
          leftName: leftWv ? displayNameFor(leftWv) : undefined,
          rightName: rightWv ? displayNameFor(rightWv) : undefined,
          leftWorldview: leftWv ? getCurrentContent(leftWv) : undefined,
          rightWorldview: rightWv ? getCurrentContent(rightWv) : undefined,
          // Carry the worldview's craft-area style settings (vocab + length)
          // into the debate so each worldview debater speaks in the register
          // its author chose, not the debate-wide default.
          leftSettings: leftWv?.settings,
          rightSettings: rightWv?.settings,
        },
      });
    }, 650);
    return () => {
      if (advanceTimerRef.current !== null) {
        clearTimeout(advanceTimerRef.current);
        advanceTimerRef.current = null;
      }
    };
    // displayNameFor reads from stageNames; safe to omit since stageNames is
    // already finalized by the time the user reaches a both-selected state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasBothSelected, leftId, rightId, navigate, worldviews]);

  /** Pick (or deselect) any debater — both historical figures and worldviews. */
  const handlePick = (id: string) => {
    if (id === leftId) { setLeftId(null); return; }
    if (id === rightId) { setRightId(null); return; }
    if (leftId === null) { setLeftId(id); return; }
    if (rightId === null) { setRightId(id); return; }
    setRightId(id);
  };

  /**
   * Selecting a custom worldview is a two-step gesture: open the naming modal
   * first, commit the stage name, then drop into handlePick. Deselecting is
   * still a single click — no modal in that direction.
   */
  const handleWorldviewClick = (wv: SavedWorldview) => {
    if (selectedIds.has(wv.id)) {
      handlePick(wv.id);
      return;
    }
    setNameDraft(stageNames[wv.id] ?? worldviewDisplayName(wv));
    setNamingId(wv.id);
  };

  const confirmNaming = () => {
    if (!namingId) return;
    const trimmed = nameDraft.trim();
    setStageNames((prev) => {
      const next = { ...prev };
      if (trimmed) next[namingId] = trimmed;
      else delete next[namingId];
      return next;
    });
    const id = namingId;
    setNamingId(null);
    handlePick(id);
  };

  const cancelNaming = () => {
    setNamingId(null);
  };

  // Focus the input when the modal opens; close it on Escape.
  useEffect(() => {
    if (!namingId) return;
    const t = window.setTimeout(() => nameInputRef.current?.select(), 30);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') cancelNaming();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
    };
  }, [namingId]);

  // Lock background scroll while the modal is open.
  useEffect(() => {
    if (!namingId) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [namingId]);

  const namingWv = namingId ? worldviews.find((w) => w.id === namingId) ?? null : null;

  return (
    <main className="scene scene--setup">
      <div
        className="scene__image scene__image--watch"
        role="img"
        aria-label="A roaring crowd packed into the colosseum stands"
      />
      <div className="scene__vignette" />

      <SceneBack />

      <div className="scene__content scene__content--setup">
        <p className="scene__eyebrow">— The Stands —</p>
        <h1 className="scene__title scene__title--setup">Who Will Debate?</h1>

        {/* Historical characters */}
        <ul className="roster" aria-label="Historical figures">
          {CHARACTERS.map((c) => {
            const isSelected = selectedIds.has(c.id);
            return (
              <li key={c.id} className="roster__item">
                <button
                  type="button"
                  className={`roster__btn${isSelected ? ' is-selected' : ''}`}
                  onClick={() => handlePick(c.id)}
                  aria-pressed={isSelected}
                  title={c.blurb}
                >
                  <span className="roster__portrait" aria-hidden="true">
                    <img src={portraitFor(c.id)} alt="" loading="lazy" />
                  </span>
                  <span className="roster__name">{c.name}</span>
                  <span className="roster__era">{c.era}</span>
                </button>
              </li>
            );
          })}
        </ul>

        {/* User worldviews */}
        {worldviews.length > 0 && (
          <div className="roster-section">
            <p className="roster-section__label">Your Worldviews</p>
            <ul className="roster" aria-label="Your worldviews">
              {worldviews.map((wv) => {
                const isSelected = selectedIds.has(wv.id);
                const name = displayNameFor(wv);
                return (
                  <li key={wv.id} className="roster__item">
                    <button
                      type="button"
                      className={`roster__btn${isSelected ? ' is-selected' : ''}`}
                      onClick={() => handleWorldviewClick(wv)}
                      aria-pressed={isSelected}
                    >
                      <span className="roster__portrait" aria-hidden="true">
                        <img src={portraitFor(wv.id, name)} alt="" loading="lazy" />
                      </span>
                      <span className="roster__name">{name}</span>
                      <span className="roster__era">Your worldview</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {hasBothSelected && left && right && (
          <p className="pair">
            <em className="pair__name">{left.name}</em>
            <span className="pair__divider" aria-hidden="true">
              vs
            </span>
            <em className="pair__name">{right.name}</em>
          </p>
        )}
      </div>

      {namingWv && (
        <div
          className="stage-name-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Name this debater"
        >
          <div
            className="stage-name-modal__backdrop"
            onClick={cancelNaming}
            aria-hidden="true"
          />
          <form
            className="stage-name-modal__panel"
            onSubmit={(e) => {
              e.preventDefault();
              confirmNaming();
            }}
          >
            <p className="stage-name-modal__eyebrow">— Enter the Arena —</p>
            <h2 className="stage-name-modal__title">Name this debater</h2>
            <p className="stage-name-modal__subtitle">
              What should we call this worldview in the debate? Anything goes —
              a stage name, a label, an alias. Viewers will see this, not the
              name on your saved worldview.
            </p>

            <input
              ref={nameInputRef}
              className="stage-name-modal__input"
              type="text"
              value={nameDraft}
              placeholder="e.g. The Pragmatist"
              onChange={(e) => setNameDraft(e.target.value)}
              maxLength={60}
              autoFocus
            />

            <div className="stage-name-modal__actions">
              <button
                type="button"
                className="stage-name-modal__cancel"
                onClick={cancelNaming}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="stage-name-modal__confirm"
                disabled={nameDraft.trim().length === 0}
              >
                Enter
              </button>
            </div>
          </form>
        </div>
      )}
    </main>
  );
}
